import React, { useMemo, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import PropTypes, { any } from 'prop-types';

import { renderDivisionPrice } from '../../utils/events';
import { getDescriptionByTypeId, getLabelByTypeId } from '../../utils/types';
import { eventGenderTypeIds, utrTypeIds } from '../../utils/eventConstants';
import styles from './eventRegForm.scss';

const CampDivisionSelectionCard = ({ division, event, clubVerificationFee, onSelectionChange, currentRegistration, errorIndices, currencySymbol }) => {
    const auth = useSelector(({ auth }) => auth);
    const coreUI = useSelector(({ coreUI }) => coreUI);
    const {
        genderTypes,
        division: {
            formatTypes,
        },
    } = coreUI.types.tms;

    const [numSignups, setNumSignups] = useState(currentRegistration.quantity || 0);
    const [playerNames, setPlayerNames] = useState(currentRegistration.playerNames || []);
    const minDivisionRegistrations = 0;
    const maxDivisionRegistrations = 99;
    const INCREASE = 'increase';
    const DECREASE = 'decrease';
    const isSanctioned = event.utrType?.value === 'sanctioned' || event.utrTypeId === utrTypeIds.SANCTIONED;

    const subtitle = useMemo(() => {
        let value;
        const isMixedOrCoedType = division.genderId === eventGenderTypeIds.ALL || division.genderId === eventGenderTypeIds.MIXED;
        const areDivisionAndUserSameGender = getLabelByTypeId(genderTypes, division.genderId)?.toLowerCase() !== auth.user.gender?.toLowerCase();
        if (!isMixedOrCoedType && areDivisionAndUserSameGender) {
            value = `${getDescriptionByTypeId(genderTypes, division.genderId)} Only`;
        }
        return value;
    }, [division, event?.utrTypeId]);

    const basePriceString = renderDivisionPrice(division, event.allowOffPlatformPayments, isSanctioned, event.workflowVersion, auth.user, null, clubVerificationFee);
    const basePrice = parseFloat(basePriceString.replace(/[^0-9.]/g, '')) || 0;

    const totalDivisionPrice = useMemo(() => basePrice * numSignups, [numSignups, basePrice]);

    const nonPowerBasePriceString = division.priceNonPowered;
    const nonPowerBasePrice = parseFloat(nonPowerBasePriceString.replace(/[^0-9.]/g, '')) || 0;

    const nonPowerDivisionPriceToDisplay = useMemo(() => {
        if (numSignups <= 1) {
            return nonPowerBasePrice;
        }
        return nonPowerBasePrice * numSignups;
    }, [numSignups, nonPowerBasePrice]);

    const powerBasePriceString = division.price;
    const powerBasePrice = parseFloat(powerBasePriceString.replace(/[^0-9.]/g, '')) || 0;

    const powerDivisionPriceToDisplay = useMemo(() => {
        if (numSignups <= 1) {
            return powerBasePrice;
        }
        return powerBasePrice * numSignups;
    }, [numSignups, powerBasePrice]);

    useEffect(() => {
        onSelectionChange(division.id, numSignups, totalDivisionPrice, playerNames);
    }, [numSignups, playerNames]);

    const handleQuantityChange = (action) => {
        setNumSignups((prev) => {
            let newQuantity = prev;
            if (action === INCREASE) {
                newQuantity = prev + 1;
                setPlayerNames((prevNames) => [...prevNames, '']);
            } else if (action === DECREASE) {
                newQuantity = prev - 1;
                setPlayerNames((prevNames) => prevNames.slice(0, -1));
            }
            return newQuantity;
        });
    };

    const handleNameChange = (index, value) => {
        setPlayerNames((prevNames) => {
            const newNames = [...prevNames];
            newNames[index] = value;
            return newNames;
        });
    };

    return (
        <div className={styles.campDivisionSelection}>
            <div className="radio-button radio-button-xl">
                <input id={`division${division.id}`} type="checkbox" />
                <div className={styles.highlight}>
                    <label htmlFor={`division${division.id}`} className={numSignups > minDivisionRegistrations ? styles.campDivisionBorder : ''}>
                        <div className="radio-button-label">
                            <div className="d-flex justify-content-between">
                                <div className="flex-1 mw0 text-break mr8">
                                    <div className="fs14 text-bold text-black">{division.name}</div>
                                    <div className="fs14 text-black">
                                        {formatTypes.find(type => type.id === parseInt(division.divisionFormatTypeId))?.label}
                                        <span> • </span>
                                        {division.ageRange}
                                        <span> • </span>
                                        {getLabelByTypeId(genderTypes, division.genderId)}
                                    </div>
                                    {subtitle && <div className="radio-button-description fs12 text-bold">{subtitle}</div>}
                                </div>
                                <div className="d-flex flex-col align-items-center">
                                    <div
                                        className={`${styles.priceText} price text-right fs16 text-bold`}
                                        style={{ textDecoration: auth.user.isPowered && isSanctioned ? 'line-through' : 'none' }}
                                    >
                                        {nonPowerDivisionPriceToDisplay > 0
                                            ? `${currencySymbol}${nonPowerDivisionPriceToDisplay.toFixed(2)}`
                                            : 'Free'}
                                    </div>
                                    {isSanctioned && (
                                    <div
                                        className={`${styles.priceText} price text-right fs12`}
                                    >
                                        {powerDivisionPriceToDisplay > 0
                                            ? `${currencySymbol}${powerDivisionPriceToDisplay.toFixed(2)}/Power`
                                            : 'Free'}
                                    </div>
                                    )}
                                </div>
                            </div>
                            <div className="d-flex align-items-center mt-2">
                                <input disabled={true} className={styles.campRegistrationQuantity} type="text" value={numSignups} />
                                <div className={`${styles.buttonContainer} d-flex align-items-center border rounded bg-light p-1`}>
                                    <button type="button" onClick={() => handleQuantityChange(DECREASE)} disabled={numSignups === minDivisionRegistrations} data-testid="event-reg.camp-division.decrease-btn">-</button>
                                    <div className={styles.separator}>|</div>
                                    <button type="button" onClick={() => handleQuantityChange(INCREASE)} disabled={numSignups === maxDivisionRegistrations} data-testid="event-reg.camp-division.increase-btn">+</button>
                                </div>
                            </div>
                            {playerNames.map((name, index) => (
                                <>
                                    <div key={`${division.id}-${index}`} className={styles.campInput}>
                                        <input
                                            className={'text-black'}
                                            type="text"
                                            value={name}
                                            onChange={(e) => handleNameChange(index, e.target.value)}
                                            required
                                        />
                                        <label>{`Player ${index + 1}`}</label>

                                    </div>
                                    {errorIndices.includes(index) && (
                                        <div className={styles.errorMessage}>
                                            Missing Name Field
                                        </div>
                                    )}
                                </>
                            ))}
                        </div>
                    </label>
                </div>
            </div>
        </div>
    );
};

CampDivisionSelectionCard.propTypes = {
    division: PropTypes.objectOf(any).isRequired,
    event: PropTypes.objectOf(any).isRequired,
    clubVerificationFee: PropTypes.number,
    onSelectionChange: PropTypes.func.isRequired,
    currentRegistration: PropTypes.shape({
        quantity: PropTypes.number,
        playerNames: PropTypes.arrayOf(PropTypes.string),
    }),
    errorIndices: PropTypes.arrayOf(PropTypes.number),
    currencySymbol: PropTypes.string.isRequired,
};

CampDivisionSelectionCard.defaultProps = {
    clubVerificationFee: 0,
    errorIndices: [],
    currentRegistration: {},
};

export default CampDivisionSelectionCard;
